// shortcutHelpers.js
//
// Shared helpers for the "Copy Link" / "Pin to desktop" UX pattern used by
// individual notes (Build 26) and by defects/feature requests (existing).
//
// The goal is to keep clipboard + permalink + shortcut-creation logic in
// one place so adding new entity types in future builds is a one-liner
// per entity, not a copy-paste of inline JSX click handlers.

import { createShortcut } from '../services/desktopService';

/**
 * Build a permalink URL for an entity.
 *
 * @param {string} routePrefix  e.g. '/notes', '/defect-tracker'
 * @param {string} id           the entity id
 * @returns {string} absolute URL safe to share / paste
 */
export function buildPermalink(routePrefix, id) {
  if (!id) return '';
  // window.location.origin gives us the correct scheme/host/port for
  // localhost dev, staging, and production without per-env config.
  return `${window.location.origin}${routePrefix}/${id}`;
}

/**
 * Copy text to the clipboard. Returns true on success, false on failure.
 * Doesn't alert — caller is responsible for user feedback.
 */
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for older browsers / non-secure contexts (e.g. http:// localhost
    // edge case). Uses the deprecated execCommand but works as a last resort.
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch (err) {
    console.error('copyToClipboard failed:', err);
    return false;
  }
}

/**
 * Pin an entity to the user's desktop as a typed shortcut.
 *
 * Wraps createShortcut() so callers don't have to assemble the shortcut
 * payload by hand each time. Note shortcuts use type='note' and noteId
 * to match the existing Desktop handler.
 *
 * @param {Object} opts
 * @param {string} opts.userId       Firebase uid of the owner
 * @param {string} opts.type         'note' (extendable to 'defect', 'feature' later)
 * @param {string} opts.entityId     The note/defect/feature id
 * @param {string} opts.title        Display title for the shortcut icon
 * @param {string} [opts.icon]       Emoji icon (defaults sensibly per type)
 * @returns {Promise<void>}
 */
export async function pinEntityToDesktop({ userId, type, entityId, title, icon }) {
  if (!userId) throw new Error('userId is required to pin to desktop');
  if (!entityId) throw new Error('entityId is required to pin to desktop');

  const defaultIcons = { note: '📝', defect: '🐛', feature: '✨' };
  const resolvedIcon = icon || defaultIcons[type] || '🔗';

  // Build the shortcut payload. The Desktop's click handler dispatches on
  // shortcut.type, so we set the appropriate ID field per type.
  const shortcut = {
    type,
    title: (title || 'Untitled').slice(0, 60),
    icon: resolvedIcon,
    // Stagger position slightly so multiple pins don't perfectly overlap.
    // The Desktop's Arrange Icons / drag-to-move flow can clean this up.
    position: { x: 50 + Math.floor(Math.random() * 40), y: 100 + Math.floor(Math.random() * 40) }
  };

  if (type === 'note')     shortcut.noteId    = entityId;
  if (type === 'defect')   shortcut.defectId  = entityId;
  if (type === 'feature')  shortcut.featureId = entityId;
  if (type === 'diagram')  shortcut.diagramId = entityId;

  await createShortcut(userId, shortcut);
}
